//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  createLike2BuyV2CustomizationsBannerCrawledProduct,
  digestLike2BuyV2CustomizationsBanner,
  updateLike2BuyV2StoreConfig,
} from '@apis/like2buy-v2/like2BuyV2CustomizationsApi';
import { isLike2BuyV2TikTokDefaultConfig } from '@apis/like2buy-v2/like2BuyV2StoreModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import {
  digestLike2BuyV2CustomizationsBannerFailedToast,
  loadLike2BuyV2CustomizationsFailedToast,
  saveLike2BuyV2CustomizationsFailedToast,
  saveLike2BuyV2CustomizationsSucceededToast,
} from '@like2buy-v2/like2BuyV2CustomizationsToasts';
import FileId from '@models/FileId';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import { SocialNetwork } from '@models/SocialNetwork';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import {
  BANNER_CONSTANTS,
  CTA_CONSTANTS,
  EMAIL_CAPTURE_CONSTANTS,
  UPLOAD_BYTES_LIMIT,
  getExceededCtaLimitWarning,
} from '@tiktok/components/like2buy/customizations/storeCustomizationsConstants';
import {
  getTikTokLike2BuyCustomizations,
  getIsBannerAltTextValid,
  getIsDestinationUrlValid,
  getIsTikTokLike2BuyCustomizationsBannerEnabled,
  getTikTokLike2BuyCustomizationsBannerDestinationUrl,
  getTikTokLike2BuyCustomizationsCtasGalleryHeader,
  getTikTokLike2BuyCustomizationsCtasGalleryMediaHover,
  getTikTokLike2BuyCustomizationsCtasProductsContainer,
  getHasUploadedBannerImage,
  getTikTokLike2BuyCustomizationsBannerImageFileId,
  getIsTermsAndConditionsUrlValid,
  getIsTikTokLike2BuyCustomizationsEmailCaptureEnabled,
  getTikTokLike2BuyCustomizationsEmailCaptureTermsAndConditionsUrl,
  getTikTokLike2BuyCustomizationsEmailCaptureCta,
  getTikTokLike2BuyCustomizationsEmailCaptureShouldRequireTermsAndConditions,
} from '@tiktok/redux/like2buy/customizations/selectors/getTikTokLike2BuyCustomizations';
import {
  DEFAULT_BANNER_CUSTOMIZATIONS,
  DEFAULT_CTA_CUSTOMIZATIONS,
  DEFAULT_EMAIL_CAPTURE_CUSTOMIZATIONS,
  ITikTokLike2BuyCustomizations,
  ITikTokLike2BuyCustomizationsState,
} from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsModel';
import {
  isCtaValid,
  tiktokStoreConfigToCustomizations,
  tiktokCustomizationsToStoreConfig,
} from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsUtils';
import {
  getTikTokLike2BuyStore,
  getTikTokLike2BuyStoreConfig,
  getTikTokLike2BuyStoreId,
} from '@tiktok/redux/like2buy/selectors/getTikTokLike2Buy';
import { receiveStoreSucceeded } from '@tiktok/redux/like2buy/tiktokLike2BuySlice';
import { isDefined, isTruthy, isUndefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { getUrlWithSecureProtocol } from '@utils/urlUtils';
import { hasValidProtocol } from '@utils/urlValidation';

const initialState: ITikTokLike2BuyCustomizationsState = {
  customizations: {
    banner: DEFAULT_BANNER_CUSTOMIZATIONS,
    ctas: DEFAULT_CTA_CUSTOMIZATIONS,
    emailCapture: DEFAULT_EMAIL_CAPTURE_CUSTOMIZATIONS,
  },
  invalidInputs: [],

  isStoreCustomizationsModalOpen: false,
  isStorePreviewModalOpen: false,
  errors: {
    createCrawledProductError: undefined,
    digestBannerImageError: undefined,
    updateStoreConfigurationError: undefined,
  },
  flags: {
    isCreatingCrawledProduct: false,
    isDigestingBannerImage: false,
    isUpdatingStoreConfiguration: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.TikTokLike2BuyCustomizations,
  initialState,
  reducers: {
    setIsStoreCustomizationsModalOpen(
      state,
      { payload: isOpen }: PayloadAction<boolean>,
    ) {
      state.isStoreCustomizationsModalOpen = isOpen;
    },
    setIsStorePreviewModalOpen(
      state,
      { payload: isOpen }: PayloadAction<boolean>,
    ) {
      state.isStorePreviewModalOpen = isOpen;
    },
    setExistingCustomizations(
      state,
      {
        payload: { config },
      }: PayloadAction<{
        config: ITikTokLike2BuyCustomizations;
      }>,
    ) {
      state.customizations = config;
    },
    setCustomizationsGalleryHeaderCta(
      state,
      { payload }: PayloadAction<string>,
    ) {
      state.customizations.ctas.galleryHeader.text = payload;
    },
    setCustomizationsGalleryHeaderCtaValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.ctas.galleryHeader.validationError = payload;
    },
    setCustomizationsGalleryMediaHoverCta(
      state,
      { payload }: PayloadAction<string>,
    ) {
      state.customizations.ctas.galleryMediaHover.text = payload;
    },
    setCustomizationsGalleryMediaHoverCtaValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.ctas.galleryMediaHover.validationError = payload;
    },
    setCustomizationsProductsContainerCta(
      state,
      { payload }: PayloadAction<string>,
    ) {
      state.customizations.ctas.productsContainer.text = payload;
    },
    setCustomizationsProductsContainerCtaValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.ctas.productsContainer.validationError = payload;
    },
    setCustomizationsBannerAltText(state, { payload }: PayloadAction<string>) {
      state.customizations.banner.altText.text = payload;
    },
    setCustomizationsBannerAltTextValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.banner.altText.validationError = payload;
    },
    setCustomizationsBannerDestinationUrl(
      state,
      { payload }: PayloadAction<string>,
    ) {
      state.customizations.banner.destinationUrl.text = payload;

      // if we are changing the url we want to ensure we won't ever save with a previous crawlId
      if (isDefined(state.customizations.banner.crawlId)) {
        state.customizations.banner.crawlId = undefined;
      }
    },
    setCustomizationsBannerDestinationUrlValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.banner.destinationUrl.validationError = payload;
    },
    setCustomizationsImageUploadValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.banner.imageUpload.validationError = payload;
    },
    setInvalidInputs(
      state,
      {
        payload,
      }: PayloadAction<ITikTokLike2BuyCustomizationsState['invalidInputs']>,
    ) {
      state.invalidInputs = payload;
    },
    requestUpdateStoreConfiguration(state) {
      state.flags.isUpdatingStoreConfiguration = true;
      state.errors.updateStoreConfigurationError = undefined;
    },
    receiveUpdateStoreConfigurationSucceeded(state) {
      state.flags.isUpdatingStoreConfiguration = false;
      state.errors.updateStoreConfigurationError = undefined;
    },
    receiveUpdateStoreConfigurationFailed(
      state,
      { payload: errorMsg }: PayloadAction<string>,
    ) {
      state.flags.isUpdatingStoreConfiguration = false;
      state.errors.updateStoreConfigurationError = errorMsg;
    },
    setIsBannerEnabled(state, { payload }: PayloadAction<boolean>) {
      state.customizations.banner.isBannerEnabled = payload;
    },
    requestDigestBannerImage(state) {
      state.flags.isDigestingBannerImage = true;
      state.errors.digestBannerImageError = undefined;
      // if we are changing the image we want to ensure we won't ever save with a previous crawlId
      state.customizations.banner.crawlId = undefined;
      state.customizations.banner.imageUpload = {};
    },
    receiveDigestBannerImageSucceeded(
      state,
      {
        payload: { imageFileName, imageFileId },
      }: PayloadAction<{
        imageFileName: string;
        imageFileId: string;
      }>,
    ) {
      state.flags.isDigestingBannerImage = false;
      state.customizations.banner.imageUpload.imageFileId = FileId(imageFileId);
      state.customizations.banner.imageUpload.imageFileName = imageFileName;
    },
    receiveDigestBannerImageFailed(state, { payload }: PayloadAction<string>) {
      state.flags.isDigestingBannerImage = false;
      state.errors.digestBannerImageError = payload;
    },
    removeBannerImage(state) {
      state.customizations.banner.crawlId = undefined;
      state.customizations.banner.imageUpload = {};
    },
    setIsEmailCaptureEnabled(state, { payload }: PayloadAction<boolean>) {
      state.customizations.emailCapture.isEnabled = payload;
    },
    setCustomizationsEmailCaptureTermsAndConditionsUrl(
      state,
      { payload }: PayloadAction<string>,
    ) {
      state.customizations.emailCapture.termsAndConditionsUrl.text = payload;
    },
    setCustomizationsEmailCaptureTermsAndConditionsUrlValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.emailCapture.termsAndConditionsUrl.validationError =
        payload;
    },
    setCustomizationsEmailCaptureShouldRequireTermsAndConditions(
      state,
      { payload }: PayloadAction<boolean>,
    ) {
      state.customizations.emailCapture.shouldRequireTermsAndConditions =
        payload;
      if (!payload) {
        state.customizations.emailCapture.termsAndConditionsUrl.validationError =
          undefined;
      }
    },
    setCustomizationsEmailCaptureCta(
      state,
      { payload }: PayloadAction<string>,
    ) {
      state.customizations.emailCapture.cta.text = payload;
    },
    setCustomizationsEmailCaptureCtaValidationError(
      state,
      { payload }: PayloadAction<string | undefined>,
    ) {
      state.customizations.emailCapture.cta.validationError = payload;
    },
  },
});

export const {
  removeBannerImage,
  setCustomizationsBannerAltText,
  setCustomizationsBannerDestinationUrl,
  setCustomizationsEmailCaptureCta,
  setCustomizationsEmailCaptureCtaValidationError,
  setCustomizationsEmailCaptureShouldRequireTermsAndConditions,
  setCustomizationsEmailCaptureTermsAndConditionsUrl,
  setCustomizationsEmailCaptureTermsAndConditionsUrlValidationError,
  setCustomizationsGalleryHeaderCta,
  setCustomizationsGalleryMediaHoverCta,
  setCustomizationsImageUploadValidationError,
  setCustomizationsProductsContainerCta,
  setIsBannerEnabled,
  setIsEmailCaptureEnabled,
  setIsStoreCustomizationsModalOpen,
  setIsStorePreviewModalOpen,
} = actions;

export const initializeCustomizations = createThunk(
  () => (dispatch, getState) => {
    const config = getTikTokLike2BuyStoreConfig(getState());

    if (!isLike2BuyV2TikTokDefaultConfig(config)) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          loadLike2BuyV2CustomizationsFailedToast(SocialNetwork.TikTok),
        ),
      );
      return false;
    }

    dispatch(
      actions.setExistingCustomizations({
        config: tiktokStoreConfigToCustomizations(config),
      }),
    );

    return true;
  },
);

export const validateDestinationUrl = createThunk(
  () => (dispatch, getState) => {
    const state = getState();

    const { text: bannerUrl } =
      getTikTokLike2BuyCustomizationsBannerDestinationUrl(state);
    const isDestinationUrlValid = getIsDestinationUrlValid(state);
    const { destinationUrl } = BANNER_CONSTANTS;

    if (isDestinationUrlValid) {
      if (!hasValidProtocol(bannerUrl)) {
        dispatch(
          actions.setCustomizationsBannerDestinationUrl(
            getUrlWithSecureProtocol(bannerUrl),
          ),
        );
      }
    } else {
      dispatch(
        actions.setCustomizationsBannerDestinationUrlValidationError(
          destinationUrl.validationErrorMessage,
        ),
      );
    }

    return isDestinationUrlValid;
  },
);

export const validateEmailCaptureTermsAndConditionsUrl = createThunk(
  () => (dispatch, getState) => {
    const state = getState();

    const { text: termsAndConditionsUrlText } =
      getTikTokLike2BuyCustomizationsEmailCaptureTermsAndConditionsUrl(state);
    const isTermsAndConditionsUrlValid = getIsTermsAndConditionsUrlValid(state);
    const shouldRequireTermsAndConditions =
      getTikTokLike2BuyCustomizationsEmailCaptureShouldRequireTermsAndConditions(
        state,
      );
    const { termsAndConditionsUrl } = EMAIL_CAPTURE_CONSTANTS;

    const validationErrorMessage: string | undefined = (() => {
      if (shouldRequireTermsAndConditions) {
        if (termsAndConditionsUrlText.length == 0) {
          return termsAndConditionsUrl.missingErrorMessage;
        } else if (!isTermsAndConditionsUrlValid) {
          return termsAndConditionsUrl.validationErrorMessage;
        } else {
          if (!hasValidProtocol(termsAndConditionsUrlText)) {
            dispatch(
              setCustomizationsEmailCaptureTermsAndConditionsUrl(
                getUrlWithSecureProtocol(termsAndConditionsUrlText),
              ),
            );
          }

          return undefined;
        }
      } else {
        return undefined;
      }
    })();

    dispatch(
      actions.setCustomizationsEmailCaptureTermsAndConditionsUrlValidationError(
        validationErrorMessage,
      ),
    );

    return !shouldRequireTermsAndConditions || isTermsAndConditionsUrlValid;
  },
);

export const validateCurrentCustomizations = createThunk(
  () => (dispatch, getState) => {
    const state = getState();
    const { text: galleryHeaderCta } =
      getTikTokLike2BuyCustomizationsCtasGalleryHeader(state);
    const { text: galleryMediaHoverCta } =
      getTikTokLike2BuyCustomizationsCtasGalleryMediaHover(state);
    const { text: productsContainerCta } =
      getTikTokLike2BuyCustomizationsCtasProductsContainer(state);

    const isBannerEnabled =
      getIsTikTokLike2BuyCustomizationsBannerEnabled(state);
    const hasUploadedBannerImage = getHasUploadedBannerImage(state);
    const isBannerAltTextValid = getIsBannerAltTextValid(state);

    const isEmailCaptureEnabled =
      getIsTikTokLike2BuyCustomizationsEmailCaptureEnabled(state);
    const { text: emailCaptureCtaText } =
      getTikTokLike2BuyCustomizationsEmailCaptureCta(state);
    const { text: termsAndConditionsUrlText } =
      getTikTokLike2BuyCustomizationsEmailCaptureTermsAndConditionsUrl(state);
    const shouldRequireTermsAndConditions =
      getTikTokLike2BuyCustomizationsEmailCaptureShouldRequireTermsAndConditions(
        state,
      );
    const isTermsAndConditionsUrlValid = dispatch(
      validateEmailCaptureTermsAndConditionsUrl(),
    );

    const invalidInputs: ITikTokLike2BuyCustomizationsState['invalidInputs'] =
      [];

    const { galleryHeader, galleryMediaHover, productsContainer } =
      CTA_CONSTANTS;
    const { bannerAltText, bannerImageUpload, destinationUrl } =
      BANNER_CONSTANTS;
    const { cta: emailCaptureCta, termsAndConditionsUrl } =
      EMAIL_CAPTURE_CONSTANTS;

    if (isEmailCaptureEnabled) {
      if (!isTermsAndConditionsUrlValid) {
        invalidInputs.push(termsAndConditionsUrl.name);
      }

      const isEmailCaptureCtaValid = isCtaValid(
        emailCaptureCtaText,
        emailCaptureCta.ctaLimit,
      );
      dispatch(
        actions.setCustomizationsEmailCaptureCtaValidationError(
          isEmailCaptureCtaValid
            ? undefined
            : getExceededCtaLimitWarning(emailCaptureCta.ctaLimit),
        ),
      );

      if (!isEmailCaptureCtaValid) {
        invalidInputs.push(emailCaptureCta.name);
      }
    }

    if (isBannerEnabled) {
      if (!dispatch(validateDestinationUrl())) {
        invalidInputs.push(destinationUrl.name);
      }

      dispatch(
        actions.setCustomizationsImageUploadValidationError(
          hasUploadedBannerImage
            ? undefined
            : bannerImageUpload.validationErrorMessage,
        ),
      );
      if (!hasUploadedBannerImage) {
        invalidInputs.push(bannerImageUpload.name);
      }

      dispatch(
        actions.setCustomizationsBannerAltTextValidationError(
          isBannerAltTextValid
            ? undefined
            : getExceededCtaLimitWarning(bannerAltText.ctaLimit),
        ),
      );
      if (!isBannerAltTextValid) {
        invalidInputs.push(bannerAltText.name);
      }
    }

    const isGalleryHeaderCtaValid = isCtaValid(
      galleryHeaderCta,
      galleryHeader.ctaLimit,
    );
    dispatch(
      actions.setCustomizationsGalleryHeaderCtaValidationError(
        isGalleryHeaderCtaValid
          ? undefined
          : getExceededCtaLimitWarning(galleryHeader.ctaLimit),
      ),
    );
    if (!isGalleryHeaderCtaValid) {
      invalidInputs.push(galleryHeader.name);
    }

    const isGalleryMediaHoverCtaValid = isCtaValid(
      galleryMediaHoverCta,
      galleryMediaHover.ctaLimit,
    );
    dispatch(
      actions.setCustomizationsGalleryMediaHoverCtaValidationError(
        isGalleryMediaHoverCtaValid
          ? undefined
          : getExceededCtaLimitWarning(galleryMediaHover.ctaLimit),
      ),
    );
    if (!isGalleryMediaHoverCtaValid) {
      invalidInputs.push(galleryMediaHover.name);
    }

    const isProductsContainerCtaValid = isCtaValid(
      productsContainerCta,
      productsContainer.ctaLimit,
    );
    dispatch(
      actions.setCustomizationsProductsContainerCtaValidationError(
        isProductsContainerCtaValid
          ? undefined
          : getExceededCtaLimitWarning(productsContainer.ctaLimit),
      ),
    );
    if (!isProductsContainerCtaValid) {
      invalidInputs.push(productsContainer.name);
    }

    dispatch(actions.setInvalidInputs(invalidInputs));

    return invalidInputs;
  },
);

export const saveCustomizations = createThunk(
  () => async (dispatch, getState) => {
    const state = getState();
    const store = getTikTokLike2BuyStore(state);
    const currentCustomizations = getTikTokLike2BuyCustomizations(state);
    const isBannerEnabled =
      getIsTikTokLike2BuyCustomizationsBannerEnabled(state);

    const existingStoreConfig = store?.config;

    if (
      !isDefined(store) ||
      !isLike2BuyV2TikTokDefaultConfig(existingStoreConfig)
    ) {
      dispatch(
        toastNotificationsActions.addToastNotification(
          saveLike2BuyV2CustomizationsFailedToast(SocialNetwork.TikTok),
        ),
      );

      return;
    }

    try {
      dispatch(actions.requestUpdateStoreConfiguration());

      // we don't want to generate a crawled product if the banner is disabled
      const response = isBannerEnabled
        ? await dispatch(createBannerCrawledProduct())
        : {
            crawlId: currentCustomizations.banner.crawlId,
            url: currentCustomizations.banner.destinationUrl.text,
          };

      const storeConfigWithCustomizations = tiktokCustomizationsToStoreConfig(
        existingStoreConfig,
        {
          ...currentCustomizations,
          banner: {
            ...currentCustomizations.banner,
            crawlId: response?.crawlId ?? '',
            destinationUrl: {
              text: response?.url ?? '',
            },
          },
        },
      );

      const updatedStore = await updateLike2BuyV2StoreConfig({
        storeId: Like2BuyV2StoreId(store.id),
        body: storeConfigWithCustomizations,
      });

      if (!isDefined(updatedStore)) {
        throw new Error(
          'Failed to update TikTok Like2Buy store when saving customizations',
        );
      }

      dispatch(receiveStoreSucceeded(updatedStore));
      dispatch(
        toastNotificationsActions.addToastNotification(
          saveLike2BuyV2CustomizationsSucceededToast(SocialNetwork.TikTok),
        ),
      );
      dispatch(actions.receiveUpdateStoreConfigurationSucceeded());
    } catch (error) {
      const errorMsg =
        getRequestErrorMessage(error) ??
        `[saveCustomizations]: Unexpected failure saving TikTok Like2Buy store customizations`;

      dispatch(actions.receiveUpdateStoreConfigurationFailed(errorMsg));
      console.error(errorMsg);

      dispatch(
        toastNotificationsActions.addToastNotification(
          saveLike2BuyV2CustomizationsFailedToast(SocialNetwork.TikTok),
        ),
      );
    }
  },
);

export const digestBannerImage = createThunk(
  (file: File) => async (dispatch, getState) => {
    const state = getState();
    const storeId = getTikTokLike2BuyStoreId(state);

    dispatch(actions.requestDigestBannerImage());

    try {
      if (isUndefined(storeId)) {
        throw new Error('storeId is required to digest banner image');
      }
      if (file.size > UPLOAD_BYTES_LIMIT) {
        throw new Error(
          BANNER_CONSTANTS.bannerImageUpload.fileSizeErrorMessage,
        );
      }

      const { fileId } = await digestLike2BuyV2CustomizationsBanner(storeId, {
        file,
      });

      dispatch(
        actions.receiveDigestBannerImageSucceeded({
          imageFileName: file.name,
          imageFileId: fileId,
        }),
      );
    } catch (e) {
      const errorMessage = getRequestErrorMessage(e);

      dispatch(
        actions.receiveDigestBannerImageFailed(
          errorMessage ??
            '[digestBannerImage] Unknown failure digesting customizations banner',
        ),
      );
      dispatch(
        toastNotificationsActions.addToastNotification(
          digestLike2BuyV2CustomizationsBannerFailedToast(SocialNetwork.TikTok),
        ),
      );

      if (
        errorMessage === BANNER_CONSTANTS.bannerImageUpload.fileSizeErrorMessage
      ) {
        dispatch(
          actions.setCustomizationsImageUploadValidationError(errorMessage),
        );
      }
    }
  },
);

export const createBannerCrawledProduct = createThunk(
  () => async (dispatch, getState) => {
    const state = getState();
    const storeId = getTikTokLike2BuyStoreId(state);
    const imageFileId = getTikTokLike2BuyCustomizationsBannerImageFileId(state);
    const { text: destinationUrl } =
      getTikTokLike2BuyCustomizationsBannerDestinationUrl(state);
    const store = getTikTokLike2BuyStore(state);

    const existingStoreConfig = store?.config;
    if (isUndefined(existingStoreConfig)) {
      // we should never hit this case because of the check in initializeCustomizations
      return;
    }

    const { galleryBannerSection, customStringsByLocale } = existingStoreConfig;
    const configCrawlId = galleryBannerSection?.crawlId;

    try {
      if (isUndefined(storeId)) {
        throw new Error('storeId is required to create crawl');
      }
      if (isUndefined(imageFileId)) {
        throw new Error('imageFileId is required to create crawl');
      }
      if (!isTruthy(destinationUrl)) {
        throw new Error('destinationUrl is required to create crawl');
      }

      if (
        isDefined(galleryBannerSection) &&
        isTruthy(configCrawlId) &&
        galleryBannerSection.imageFileId === imageFileId &&
        customStringsByLocale['en-us']?.gallery?.header?.bannerSection
          ?.destinationUrl === destinationUrl
      ) {
        return { crawlId: configCrawlId, url: destinationUrl };
      }

      const { crawlId, url } =
        await createLike2BuyV2CustomizationsBannerCrawledProduct(storeId, {
          fileId: imageFileId,
          url: destinationUrl,
        });

      return { crawlId, url };
    } catch (e) {
      throw new Error(
        getRequestErrorMessage(e) ??
          '[createBannerCrawledProduct] Unknown failure creating a crawled product for customizations banner',
      );
    }
  },
);

export { reducer };
